// src/lib/db.js  — Firestore CRUD helpers
import {
  collection, doc, getDocs, getDoc, addDoc, updateDoc, deleteDoc,
  setDoc, query, where, orderBy, serverTimestamp, writeBatch, increment,
  runTransaction, Timestamp,
} from "firebase/firestore";
import { db } from "./firebase";

// ── DESIGNERS ────────────────────────────────────────────────
export async function getDesigners() {
  const snap = await getDocs(query(collection(db, "designers"), orderBy("name")));
  return snap.docs.map(d => ({ id: d.id, ...d.data() }));
}

export async function getDesigner(uid) {
  const snap = await getDoc(doc(db, "designers", uid));
  return snap.exists() ? { id: snap.id, ...snap.data() } : null;
}

const portalMemberDefaults = () => ({
  tier: "Partner",
  commissionRate: 8,
  ytdSales: 0,
  ytdEarned: 0,
  pendingPayout: 0,
  totalPaid: 0,
  status: "active",
  statsPeriodStart: serverTimestamp(),
  createdAt: serverTimestamp(),
});

export async function createDesigner(uid, data) {
  const commissionPending = data.commissionPending !== undefined ? data.commissionPending : true;
  await setDoc(doc(db, "designers", uid), {
    uid,
    ...portalMemberDefaults(),
    ...data,
    commissionPending,
    statsPeriodStart: serverTimestamp(),
    createdAt: serverTimestamp(),
  });
}

export async function updateDesigner(uid, data) {
  await updateDoc(doc(db, "designers", uid), { ...data, updatedAt: serverTimestamp() });
}

/**
 * Auth user signed in but has no `designers/{uid}` or `contractors/{uid}` doc (e.g. Auth-only user from Console).
 * Creates the same starter designer profile as self-registration so login works without manual Firestore edits.
 * Requires a non-empty auth email (Firestore rules). Returns whether a new doc was written.
 */
export async function ensurePortalDesignerFromAuthUser(firebaseUser) {
  const uid = firebaseUser?.uid;
  if (!uid) return false;
  const emailRaw = firebaseUser.email;
  const email = typeof emailRaw === "string" ? emailRaw.trim().toLowerCase() : "";
  if (!email) return false;

  const dRef = doc(db, "designers", uid);
  const cRef = doc(db, "contractors", uid);
  const [dSnap, cSnap] = await Promise.all([getDoc(dRef), getDoc(cRef)]);
  if (dSnap.exists() || cSnap.exists()) return false;

  const name =
    (typeof firebaseUser.displayName === "string" && firebaseUser.displayName.trim()) ||
    email.split("@")[0] ||
    "Designer";

  await createDesigner(uid, {
    name,
    email,
    firm: "",
    phone: "",
    commissionPending: true,
  });
  return true;
}

// ── CONTRACTORS (same schema & portal features as designers) ─
export async function getContractors() {
  const snap = await getDocs(query(collection(db, "contractors"), orderBy("name")));
  return snap.docs.map(d => ({ id: d.id, ...d.data() }));
}

export async function createContractor(uid, data) {
  const commissionPending = data.commissionPending !== undefined ? data.commissionPending : true;
  await setDoc(doc(db, "contractors", uid), {
    uid,
    ...portalMemberDefaults(),
    ...data,
    commissionPending,
    statsPeriodStart: serverTimestamp(),
    createdAt: serverTimestamp(),
  });
}

export async function updateContractor(uid, data) {
  await updateDoc(doc(db, "contractors", uid), { ...data, updatedAt: serverTimestamp() });
}

/** Firestore doc ref for designers/{uid} or contractors/{uid}, whichever exists. */
export async function portalMemberDocRef(uid) {
  const dRef = doc(db, "designers", uid);
  const dSnap = await getDoc(dRef);
  if (dSnap.exists()) return dRef;
  const cRef = doc(db, "contractors", uid);
  const cSnap = await getDoc(cRef);
  if (cSnap.exists()) return cRef;
  return null;
}

function addOneYear(d) {
  const x = new Date(d.getTime());
  x.setFullYear(x.getFullYear() + 1);
  return x;
}

/**
 * Resets ytdSales & ytdEarned each anniversary of statsPeriodStart (or createdAt).
 * Call after loading a portal profile (designer or contractor).
 */
export async function ensureProgramYearRollover(uid, collectionName) {
  const ref = doc(db, collectionName, uid);
  await runTransaction(db, async (transaction) => {
    const snap = await transaction.get(ref);
    if (!snap.exists()) return;
    const data = snap.data();
    const created = data.createdAt?.toDate?.() ?? new Date();
    let periodStart = data.statsPeriodStart?.toDate?.() ?? created;
    const now = new Date();
    let ytdSales = data.ytdSales ?? 0;
    let ytdEarned = data.ytdEarned ?? 0;
    let changed = false;
    while (true) {
      const periodEnd = addOneYear(periodStart);
      if (periodEnd > now) break;
      periodStart = periodEnd;
      ytdSales = 0;
      ytdEarned = 0;
      changed = true;
    }
    if (changed) {
      transaction.update(ref, {
        statsPeriodStart: Timestamp.fromDate(periodStart),
        ytdSales,
        ytdEarned,
        updatedAt: serverTimestamp(),
      });
    }
  });
}

/** Sort key for Firestore Timestamp-like fields (client-side sort avoids composite indexes on equality queries). */
function millisFromFirestoreTs(ts) {
  if (!ts) return 0;
  if (typeof ts.toMillis === "function") return ts.toMillis();
  if (typeof ts.seconds === "number") return ts.seconds * 1000;
  return 0;
}

// ── ORDERS ───────────────────────────────────────────────────
export async function getOrders(designerUid = null) {
  if (designerUid) {
    const q = query(collection(db, "orders"), where("designerUid", "==", designerUid));
    const snap = await getDocs(q);
    const rows = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
    rows.sort((a, b) => millisFromFirestoreTs(b.createdAt) - millisFromFirestoreTs(a.createdAt));
    return rows;
  }
  const q = query(collection(db, "orders"), orderBy("createdAt", "desc"));
  const snap = await getDocs(q);
  return snap.docs.map((d) => ({ id: d.id, ...d.data() }));
}

export const ORDER_STATUS_PENDING_APPROVAL = "pending_approval";

export function tierPlainText(t) {
  if (t == null || t === "") return "Partner";
  if (typeof t === "object") {
    if (t.label != null) return String(t.label);
    if (t.key != null) return String(t.key);
    return "Partner";
  }
  return String(t);
}

/** Tier row index from YTD sales vs program tier thresholds (minSales ascending). */
export function tierIndexForSales(sales, tiers) {
  const list = [...(tiers || [])].sort((a, b) => (a.minSales ?? 0) - (b.minSales ?? 0));
  if (!list.length) return 0;
  let idx = 0;
  list.forEach((row, i) => {
    if (sales >= (row.minSales ?? 0)) idx = i;
  });
  return idx;
}

/** Next calendar anniversary used for program-year rollover display (365-day cycles from statsPeriodStart or createdAt). */
export function nextAnnualResetDate(profile) {
  const start = profile?.statsPeriodStart?.toDate?.() ?? profile?.createdAt?.toDate?.() ?? new Date();
  let boundary = new Date(start.getTime());
  boundary.setFullYear(boundary.getFullYear() + 1);
  const now = new Date();
  while (boundary <= now) boundary.setFullYear(boundary.getFullYear() + 1);
  return boundary;
}

/** Progress toward next tier for admin lists / dashboards. */
export function tierProgressForMember(member, programSettings) {
  const tiers = programSettings?.tiers?.length ? programSettings.tiers : defaultSettings.tiers;
  const ytd = Number(member.ytdSales) || 0;
  const name = tierPlainText(member.tier);
  const idx = tiers.findIndex((t) => t.name === name);
  const safeIdx = idx >= 0 ? idx : 0;
  const next = tiers[safeIdx + 1];
  if (!next) return { progressPct: 100, label: "Top tier", toGo: 0, nextName: null, nextRate: null };
  const minNext = next.minSales ?? 0;
  const prevMin = tiers[safeIdx].minSales ?? 0;
  const span = Math.max(1, minNext - prevMin);
  const prog = Math.min(100, Math.round(((ytd - prevMin) / span) * 100));
  const toGo = Math.max(0, minNext - ytd);
  return {
    progressPct: prog,
    label: `${prog}% · $${toGo.toLocaleString()} to ${next.name}`,
    toGo,
    nextName: next.name,
    nextRate: next.rate,
  };
}

function randomInviteToken(len = 28) {
  const chars = "abcdefghijklmnopqrstuvwxyz0123456789";
  let s = "";
  for (let i = 0; i < len; i++) s += chars[Math.floor(Math.random() * chars.length)];
  return s;
}

/**
 * Effective commission % for portal math and display.
 * Returns null when showroom has not finalized commission (`commissionPending`).
 */
export function commissionRateForDesignerProfile(designer, programSettings) {
  if (!designer) return defaultSettings.tiers[0].rate;
  if (designer.commissionPending === true) return null;
  const tiers = programSettings?.tiers?.length ? programSettings.tiers : defaultSettings.tiers;
  const tierName = tierPlainText(designer.tier);
  const tierRow = tiers.find(x => x.name === tierName);
  if (tierRow?.rate != null) return tierRow.rate;
  const profile = Number(designer.commissionRate);
  if (Number.isFinite(profile) && profile > 0) return profile;
  return defaultSettings.tiers[0].rate;
}

/** Designer/contractor logged sale — enters pipeline as `new` (no admin gate per order). */
export async function submitDesignerLoggedOrder(payload) {
  const commission = Math.round((payload.amount * payload.commissionRate) / 100);
  const docRef = await addDoc(collection(db, "orders"), {
    orderNumber: payload.orderNumber,
    projectId: payload.projectId,
    designerUid: payload.designerUid,
    designerName: payload.designerName,
    clientName: payload.clientName,
    description: payload.description || "",
    amount: payload.amount,
    commissionRate: payload.commissionRate,
    commission,
    status: "new",
    submittedByDesigner: true,
    createdAt: serverTimestamp(),
  });
  return docRef.id;
}

/** @deprecated Use submitDesignerLoggedOrder — kept for older bundles if any. */
export const submitDesignerPendingOrder = submitDesignerLoggedOrder;

/**
 * Queues an email for the Firebase "Trigger Email" extension (collection `mail`).
 * Set Program Settings → Order approval email, or ensure contactEmail is set.
 */
export async function queueAdminOrderApprovalEmail(orderId, summary) {
  const settings = await getSettings();
  const raw = (settings.orderApprovalEmail || settings.contactEmail || "").trim();
  const to = raw.split(/[,;]+/).map(s => s.trim()).filter(Boolean);
  if (!to.length) return;
  const subject = `[Action needed] Order pending approval: ${summary.orderNumber}`;
  const text = [
    "A designer or contractor submitted an order for approval.",
    "",
    `Document ID: ${orderId}`,
    `Sales order #: ${summary.orderNumber}`,
    `Project ID: ${summary.projectId}`,
    `Submitted by: ${summary.designerName}`,
    `Client / project: ${summary.clientName}`,
    `Amount (excl. tax/shipping): $${Number(summary.amount).toLocaleString()}`,
    "",
    "Open Admin → Orders to approve or reject.",
  ].join("\n");
  await addDoc(collection(db, "mail"), {
    to,
    message: { subject, text },
    orderId,
  });
}

export async function createOrder(data) {
  const { attachmentFile: _ignore, ...rest } = data;
  const commission = Math.round((rest.amount * rest.commissionRate) / 100);
  const payload = {
    ...rest,
    commission,
    status: rest.status || "new",
    submittedByDesigner: false,
    createdAt: serverTimestamp(),
  };
  if (!payload.projectId) delete payload.projectId;
  if (!payload.attachmentUrl) delete payload.attachmentUrl;
  if (!payload.attachmentName) delete payload.attachmentName;
  const docRef = await addDoc(collection(db, "orders"), payload);
  await applyProgramYearCreditForOrderIfEligible(docRef.id);
  return docRef.id;
}

/**
 * Credits designer/contractor program-year totals when an order reaches a qualifying status.
 * Runs at most once per order (`programYearCreditApplied`).
 * Qualifying: `delivered` or `paid` (admins often skip straight to paid).
 */
async function applyProgramYearCreditForOrderIfEligible(orderId) {
  const ref = doc(db, "orders", orderId);
  const snap = await getDoc(ref);
  if (!snap.exists()) return;
  const order = snap.data();
  if (order.programYearCreditApplied === true) return;
  const status = order.status;
  if (status !== "delivered" && status !== "paid") return;

  const sellerUid = order.designerUid;
  const mRef = await portalMemberDocRef(sellerUid);
  if (!mRef) return;

  const settings = await getSettings();
  const tiers = settings.tiers?.length ? settings.tiers : defaultSettings.tiers;

  const memberSnap = await getDoc(mRef);
  const m = memberSnap.data();
  const prevYtd = Number(m.ytdSales) || 0;
  const commissionPending = m.commissionPending === true;
  const newYtd = prevYtd + Number(order.amount);

  const idxOld = tierIndexForSales(prevYtd, tiers);
  const idxNew = tierIndexForSales(newYtd, tiers);
  const tierNewRow = tiers[idxNew] ?? tiers[tiers.length - 1];

  const prizePatch = {};
  if (!m.prizePartnerPreferredPending && !m.prizePartnerPreferredFulfilled && idxOld < 1 && idxNew >= 1) {
    prizePatch.prizePartnerPreferredPending = true;
  }
  if (!m.prizePreferredPrincipalPending && !m.prizePreferredPrincipalFulfilled && idxOld < 2 && idxNew >= 2) {
    prizePatch.prizePreferredPrincipalPending = true;
  }
  if (!m.prizeHalfMillionPending && !m.prizeHalfMillionFulfilled && prevYtd < 500000 && newYtd >= 500000 && idxNew >= 2) {
    prizePatch.prizeHalfMillionPending = true;
  }

  const batch = writeBatch(db);

  const memberPatch = {
    ytdSales: increment(order.amount),
    ytdEarned: increment(order.commission),
    pendingPayout: increment(order.commission),
    updatedAt: serverTimestamp(),
    ...prizePatch,
  };

  if (!commissionPending && tierNewRow) {
    memberPatch.tier = tierNewRow.name;
    memberPatch.commissionRate = tierNewRow.rate;
  }

  batch.update(mRef, memberPatch);

  const referredByUid = m.referredByUid;
  const inviteTok = m.referrerInviteToken;
  if (referredByUid && inviteTok && typeof inviteTok === "string") {
    const invRef = doc(db, "invite_links", inviteTok);
    const invSnap = await getDoc(invRef);
    if (invSnap.exists()) {
      const inv = invSnap.data();
      if (inv.refereeUid === sellerUid) {
        let bonusStartsAt = inv.bonusStartsAt;
        let bonusEndsAt = inv.bonusEndsAt;
        const invPatch = {};

        if (!bonusStartsAt) {
          bonusStartsAt = Timestamp.now();
          const endD = bonusStartsAt.toDate();
          endD.setMonth(endD.getMonth() + 6);
          bonusEndsAt = Timestamp.fromDate(endD);
          invPatch.bonusStartsAt = bonusStartsAt;
          invPatch.bonusEndsAt = bonusEndsAt;
        }

        const nowMs = Timestamp.now().toMillis();
        const endMs = bonusEndsAt?.toMillis?.() ?? 0;
        if (endMs && nowMs <= endMs) {
          const bonusAmt = Math.round(Number(order.amount) * 5 / 1000);
          if (bonusAmt > 0) {
            const referrerRef = await portalMemberDocRef(referredByUid);
            if (referrerRef) {
              batch.update(referrerRef, {
                ytdEarned: increment(bonusAmt),
                pendingPayout: increment(bonusAmt),
                updatedAt: serverTimestamp(),
              });
              invPatch.referrerBonusEarnedTotal = increment(bonusAmt);
            }
          }
        }
        if (Object.keys(invPatch).length > 0) {
          invPatch.updatedAt = serverTimestamp();
          batch.update(invRef, invPatch);
        }
      }
    }
  }

  batch.update(ref, { programYearCreditApplied: true, updatedAt: serverTimestamp() });
  await batch.commit();
}

export async function updateOrder(id, data) {
  const ref = doc(db, "orders", id);
  await updateDoc(ref, { ...data, updatedAt: serverTimestamp() });
  await applyProgramYearCreditForOrderIfEligible(id);
}

// ── COMMISSIONS / PAYOUTS ────────────────────────────────────
export async function markPaid(designerUid, amount) {
  const batch = writeBatch(db);
  const ref = await addDoc(collection(db, "commissions"), {
    designerUid,
    amount,
    paidAt: serverTimestamp(),
    status: "paid",
  });
  const mRef = await portalMemberDocRef(designerUid);
  if (mRef) {
    batch.update(mRef, {
      pendingPayout: 0,
      totalPaid: increment(amount),
    });
  }
  await batch.commit();
}

export async function getCommissions(designerUid = null) {
  if (designerUid) {
    const q = query(collection(db, "commissions"), where("designerUid", "==", designerUid));
    const snap = await getDocs(q);
    const rows = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
    rows.sort((a, b) => millisFromFirestoreTs(b.paidAt) - millisFromFirestoreTs(a.paidAt));
    return rows;
  }
  const q = query(collection(db, "commissions"), orderBy("paidAt", "desc"));
  const snap = await getDocs(q);
  return snap.docs.map((d) => ({ id: d.id, ...d.data() }));
}

// ── REFERRALS ────────────────────────────────────────────────
export async function submitReferral(data) {
  return addDoc(collection(db, "referrals"), {
    ...data,
    status: "new",
    createdAt: serverTimestamp(),
  });
}

export async function getReferrals() {
  const snap = await getDocs(query(collection(db, "referrals"), orderBy("createdAt", "desc")));
  return snap.docs.map(d => ({ id: d.id, ...d.data() }));
}

/** Designer/contractor portal: own client referrals (no composite index; sorted client-side). */
export async function getReferralsForDesigner(designerUid) {
  if (!designerUid) return [];
  const q = query(collection(db, "referrals"), where("designerUid", "==", designerUid));
  const snap = await getDocs(q);
  const rows = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
  rows.sort((a, b) => millisFromFirestoreTs(b.createdAt) - millisFromFirestoreTs(a.createdAt));
  return rows;
}

export async function updateReferral(id, data) {
  await updateDoc(doc(db, "referrals", id), { ...data, updatedAt: serverTimestamp() });
}

// ── DESIGNER → DESIGNER INVITES (+0.5% referrer bonus for 6 months after referee’s first delivered sale) ─

export async function getInviteLinkPreview(token) {
  if (!token || String(token).length < 12) return null;
  const snap = await getDoc(doc(db, "invite_links", token));
  if (!snap.exists()) return null;
  const d = snap.data();
  return { inviteeEmail: d.inviteeEmail, referrerName: d.referrerName };
}

export async function createDesignerInvite(referrerUid, referrerName, inviteeEmail) {
  const token = randomInviteToken(28);
  const norm = inviteeEmail.trim().toLowerCase();
  const batch = writeBatch(db);
  batch.set(doc(db, "invite_links", token), {
    referrerUid,
    referrerName,
    inviteeEmail: norm,
    refereeUid: null,
    referrerBonusEarnedTotal: 0,
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  });
  batch.set(doc(db, "designers", referrerUid, "invite_outbox", token), {
    inviteeEmail: norm,
    token,
    createdAt: serverTimestamp(),
  });
  await batch.commit();
  return token;
}

export async function listDesignerInviteOutbox(referrerUid) {
  const snap = await getDocs(query(collection(db, "designers", referrerUid, "invite_outbox"), orderBy("createdAt", "desc")));
  const rows = [];
  for (const d of snap.docs) {
    const token = d.id;
    const link = await getDoc(doc(db, "invite_links", token));
    if (link.exists()) rows.push({ token, ...link.data() });
  }
  return rows;
}

/** Progress UI for 6‑month referrer bonus window. */
export function inviteBonusProgress(inv) {
  if (!inv?.refereeUid) return { phase: "pending_signup", pct: 0, label: "Waiting for invitee to register" };
  if (!inv.bonusStartsAt) return { phase: "await_sale", pct: 0, label: "Waiting for first delivered sale to start bonus clock" };
  const now = Date.now();
  const start = inv.bonusStartsAt.toMillis?.() ?? 0;
  const end = inv.bonusEndsAt?.toMillis?.() ?? start;
  if (!end || now > end) return { phase: "expired", pct: 100, label: "Bonus window ended" };
  const pct = Math.min(100, Math.max(0, Math.round(((now - start) / Math.max(1, end - start)) * 100)));
  return { phase: "active", pct, label: `${pct}% through 6‑month bonus`, endsAt: inv.bonusEndsAt };
}

export async function claimDesignerInvite(token, refereeUid, refereeEmail, memberCollection = "designers") {
  const norm = refereeEmail.trim().toLowerCase();
  const invRef = doc(db, "invite_links", token);
  let referrerUidFromInvite = null;
  await runTransaction(db, async (transaction) => {
    const invSnap = await transaction.get(invRef);
    if (!invSnap.exists()) throw new Error("Invalid or expired invitation link.");
    const inv = invSnap.data();
    if (inv.refereeUid != null) throw new Error("This invitation was already accepted.");
    if (inv.inviteeEmail !== norm) throw new Error("Sign up with the same email address the invitation was sent to.");
    referrerUidFromInvite = inv.referrerUid;
    transaction.update(invRef, {
      refereeUid,
      acceptedAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    });
  });
  await updateDoc(doc(db, memberCollection, refereeUid), {
    referredByUid: referrerUidFromInvite,
    referrerInviteToken: token,
    updatedAt: serverTimestamp(),
  });
}

// ── SETTINGS ─────────────────────────────────────────────────
export async function getSettings() {
  const snap = await getDoc(doc(db, "settings", "program"));
  if (!snap.exists()) return defaultSettings;
  const raw = snap.data();
  const merged = { ...defaultSettings, ...raw };
  if (Array.isArray(raw.tiers)) {
    merged.tiers = raw.tiers.map((t, i) => ({
      ...t,
      ...(!Object.prototype.hasOwnProperty.call(t, "tierReachBonus")
        ? { tierReachBonus: defaultSettings.tiers[i]?.tierReachBonus ?? "" }
        : {}),
    }));
  }
  return merged;
}

export async function saveSettings(data) {
  await setDoc(doc(db, "settings", "program"), data);
}

export const defaultSettings = {
  tiers: [
    {
      name: "Partner",
      rate: 8,
      minSales: 0,
      maxSales: 49999,
      tierReachBonus: "",
    },
    {
      name: "Preferred",
      rate: 10,
      minSales: 50000,
      maxSales: 99999,
      tierReachBonus:
        "Milestone prize when you qualify for this tier—the showroom coordinates fulfillment.",
    },
    {
      name: "Principal",
      rate: 12,
      minSales: 100000,
      maxSales: null,
      tierReachBonus:
        "Additional milestone prize at this tier. At $500K program-year sales you may qualify for an extra award while at the top tier.",
    },
  ],
  paymentSchedule: "Monthly (1st of month)",
  minimumPayout: 250,
  commissionTrigger: "Order marked as Delivered",
  returnPolicy: "Commission reversed if returned within 30 days",
  taxFormNote: "W-9 required before first payout",
  contactEmail: "designers@david-meyer.com",
  /** Comma-separated OK; used for pending designer order notifications (Trigger Email → `mail`). */
  orderApprovalEmail: "",
};

// Seed initial admin (call once from browser console):
// import { doc, setDoc } from "firebase/firestore"; import { db } from "./firebase";
// setDoc(doc(db, "admins", "YOUR_UID"), { email: "you@david-meyer.com", createdAt: new Date() });
